import { useCallback } from 'react';
import type { RefObject } from 'react';
import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type {
	ExtensionConfiguration,
	SelectionExtensionPlugin,
	ToolbarExtensionConfiguration,
} from '@atlaskit/editor-plugin-selection-extension';
import TemplateIcon from '@atlaskit/icon-lab/core/template';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

import { BlocksSnippetAri } from '@atlassian/ari';

import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import { expVal } from '@confluence/feature-experiments';

import { messages } from '../messages';
import { CreateSnippetModal, type CreateSnippetModalProps } from '../modals/CreateSnippetModal';
import { type Snippet, SnippetContentFormat, SnippetProduct } from '../snippetTypes';
import { addSnippetQuickInsertItem } from '../quick-insert/addSnippetQuickInsertItem';

export type UseCreateSnippetExtensionProps = {
	editorAPIRef: RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>;
};

export const useCreateSnippetExtension = ({
	editorAPIRef,
}: UseCreateSnippetExtensionProps): ExtensionConfiguration[] => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showModal } = useDialogs();

	const fireCreateSnippetButtonClickedAnalytics = useCallback(
		(selectionLength: number) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'confluenceHighlightActionsMenu',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'createSnippet',
					type: 'ui',
					attributes: {
						selectionLength,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const handleCreateSnippet = useCallback(() => {
		if (!editorAPIRef?.current) {
			return;
		}

		const actions = editorAPIRef?.current?.selectionExtension?.actions;
		const document = actions?.getDocumentFromSelection();
		const docAdf: ADFEntity | undefined = document?.selectedNodeAdf;

		// getDocumentFromSelection will always return adf of type 'doc', so strip this doc wrapper for the saved content;
		const adfContent = docAdf?.content?.filter((content) => content !== undefined);

		if (!adfContent || adfContent.length === 0) {
			return;
		}

		const onSnippetCreate: CreateSnippetModalProps['onCreate'] = (
			snippetName: string,
			snippetDescription: string,
		) => {
			// In the future, we will use the response from the create endpoint most likely.
			// When that is implemented this will be removed
			const testSnippet: Snippet = {
				ari: BlocksSnippetAri.create({
					siteId: 'test-site-id',
					workspaceId: 'test-workspace',
					snippetId: '5c03eba4-7886-7f32-a0e3-af11d571f573',
				}),
				name: snippetName,
				description: snippetDescription,
				content: adfContent.length === 1 ? adfContent[0] : adfContent,
				contentFormat: SnippetContentFormat.ADF,
				createdAt: new Date(),
				createdBy: {
					accountId: 'atlassian',
					displayName: 'Atlassian',
				},
				isFavorite: false,
				product: SnippetProduct.CONFLUENCE,
				snippetId: 'test-id',
				updatedAt: undefined,
			};

			addSnippetQuickInsertItem({ snippet: testSnippet, editorAPIRef });
		};

		fireCreateSnippetButtonClickedAnalytics(JSON.stringify(adfContent)?.length);
		showModal(CreateSnippetModal, { adf: docAdf, onCreate: onSnippetCreate });
	}, [editorAPIRef, fireCreateSnippetButtonClickedAnalytics, showModal]);

	if (!expVal('cc_snippets', 'isEnabled', false)) {
		return [];
	}

	const toolbarConfiguration: ToolbarExtensionConfiguration = {
		getMenuItems: () => {
			return [
				{
					label: formatMessage(messages.createTemplate),
					lozenge: {
						label: formatMessage(messages.createTemplateLozenge),
					},
					icon: TemplateIcon,
					onClick: () => {
						void handleCreateSnippet();
					},
				},
			];
		},
	};

	return [
		{
			key: 'create-snippet',
			source: 'first-party',
			inlineToolbar: toolbarConfiguration,
		},
	];
};
